import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, CheckCircle2Icon, RefreshCwIcon, SmartphoneIcon, BuildingIcon, ChevronDownIcon, FingerprintIcon, ScanFaceIcon, ShareIcon, CopyIcon, ZapIcon, CheckIcon, XIcon, AlertTriangleIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
import { RaastHome } from './raast/RaastHome';
import { RaastRegister } from './raast/RaastRegister';
import { RaastRelinkDelink } from './raast/RaastRelinkDelink';
import { RaastMerchantPayments } from './raast/RaastMerchantPayments';
import { RaastQrTill } from './raast/RaastQrTill';
import { RaastHistory } from './raast/RaastHistory';
import { RaastRequests } from './raast/RaastRequests';
export type RaastView = 'hub' | 'register' | 'relink' | 'merchant' | 'qr_till' | 'history' | 'requests' | 'transfer';
interface RaastScreenProps {
  onClose: () => void;
  onNavigateHome?: () => void;
  initialView?: RaastView;
}
type View = RaastView;
type Step = 'tabs' | 'enter_amount' | 'review' | 'mpin' | 'processing' | 'success';
type TopTab = 'transfer' | 'management';
type TransferMethod = 'raast_id' | 'account' | 'iban';
const PAY_FROM_ACCOUNTS = [{
  id: 'current',
  holder: 'Kashish',
  label: 'Current Account',
  number: '......0123 4567 8910',
  balance: 282992
}, {
  id: 'savings',
  holder: 'Kashish',
  label: 'Savings Account',
  number: '......0456 7890 1234',
  balance: 45500
}];
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    }
  })
};
let biometricCounter = 0;
export function RaastScreen({
  onClose,
  onNavigateHome,
  initialView = 'hub'
}: RaastScreenProps) {
  const {
    isDark,
    isPlatinum
  } = useTheme();
  const [view, setView] = useState<View>(initialView);
  const [step, setStep] = useState<Step>('tabs');
  const [direction, setDirection] = useState(1);
  // Tabs State
  const [activeTopTab, setActiveTopTab] = useState<TopTab>('transfer');
  const [transferMethod, setTransferMethod] = useState<TransferMethod>('raast_id');
  // RAAST ID State
  const [raastId, setRaastId] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [fetchedDetails, setFetchedDetails] = useState<{
    name: string;
    mobile: string;
  } | null>(null);
  // Amount State
  const [amount, setAmount] = useState('');
  const [payFromAccount, setPayFromAccount] = useState(PAY_FROM_ACCOUNTS[0]);
  const [showPayFromDropdown, setShowPayFromDropdown] = useState(false);
  // Auth State
  const [biometricPhase, setBiometricPhase] = useState<'scanning' | 'verified'>('scanning');
  const [scanType, setScanType] = useState<'face' | 'thumb'>('face');
  // RAAST ID Management state
  const [isRaastIdEnabled, setIsRaastIdEnabled] = useState(true);
  const [showToggleConfirm, setShowToggleConfirm] = useState(false);
  const [showManageSheet, setShowManageSheet] = useState(false);
  const [linkedAccountId, setLinkedAccountId] = useState<string>(PAY_FROM_ACCOUNTS[0].id);
  const [pendingAccountId, setPendingAccountId] = useState<string | null>(null);
  const [showLinkSuccess, setShowLinkSuccess] = useState<{
    accountLabel: string;
  } | null>(null);
  const linkedAccount = PAY_FROM_ACCOUNTS.find(a => a.id === linkedAccountId) || PAY_FROM_ACCOUNTS[0];
  const handleBackToHub = () => setView('hub');
  const handleGoHome = () => {
    if (onNavigateHome) onNavigateHome();else onClose();
  };
  const navigateTo = (nextStep: Step, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  // Theme styles
  const bgStyle = isPlatinum ? 'bg-[#1C1C1E]' : isDark ? 'bg-[#1A0E08]' : 'bg-page-warm';
  const cardBg = isPlatinum ? 'bg-[#2A2A2C]' : isDark ? 'bg-[#2D1810]' : 'bg-white';
  const inputBg = isPlatinum ? 'bg-[#3A3A3C]' : isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]';
  const textPri = isPlatinum || isDark ? 'text-white' : 'text-[#303030]';
  const textSec = isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#B8A89E]' : 'text-[#818285]';
  const borderClr = isPlatinum ? 'border-[rgba(160,160,165,0.15)]' : isDark ? 'border-[rgba(243,112,36,0.1)]' : 'border-[#F0E8E3]';
  const handleProceedRaastId = () => {
    setIsFetching(true);
    setTimeout(() => {
      setIsFetching(false);
      setFetchedDetails({
        name: 'Ahmed Khan',
        mobile: `03** ***${raastId.slice(-4)}`
      });
    }, 1500);
  };
  useEffect(() => {
    if (step === 'mpin') {
      setBiometricPhase('scanning');
      const currentScanType = biometricCounter % 2 === 0 ? 'face' : 'thumb';
      setScanType(currentScanType);
      biometricCounter++;
      const timer1 = setTimeout(() => {
        setBiometricPhase('verified');
        const timer2 = setTimeout(() => {
          navigateTo('processing');
        }, 800);
        return () => clearTimeout(timer2);
      }, 2000);
      return () => clearTimeout(timer1);
    }
  }, [step]);
  useEffect(() => {
    if (step === 'processing') {
      const timer = setTimeout(() => {
        navigateTo('success');
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [step]);
  const renderTabs = () => {
    const isValidRaastId = raastId.length === 11 && raastId.startsWith('03');
    return <motion.div key="tabs" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2838" data-mp="z872jhlggrok">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-2839" data-mp="6j9k9eh8uuvt">
          <button onClick={handleBackToHub} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2840" data-mp="26kr78fone803">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-2841" data-mp="2gbc706g25ikl" />
          </button>
          <h1 className={`text-lg font-bold flex-1 ml-3 ${textPri}`} data-id="element-2842" data-mp="2layj2ck5gsrt">
            RAAST Transfer
          </h1>
          <button onClick={handleGoHome} className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${isDark ? 'border-[#F37024] text-[#F37024] hover:bg-[#F37024]/10' : 'border-[#5A1408] text-[#5A1408] hover:bg-[#5A1408]/5'}`} data-id="element-2843" data-mp="1nnyf4mr7vd7c">
            <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-2844" data-mp="21nr1k5vpn79i" />
          </button>
        </div>

        {/* Top Tabs */}
        <div className="px-6 mb-6" data-id="element-2845" data-mp="38zq48jzboav3">
          <div className={`flex p-1 rounded-full ${inputBg}`} data-id="element-2846" data-mp="3d1uvjc2asvek">
            <button onClick={() => {
            setActiveTopTab('transfer');
            setFetchedDetails(null);
          }} className={`flex-1 py-2.5 rounded-full text-[13px] font-bold transition-all ${activeTopTab === 'transfer' ? 'bg-[#F37024] text-white shadow-sm' : textSec}`} data-id="element-2847" data-mp="3d0a1ront5ghr">
              RAAST Transfer
            </button>
            <button onClick={() => setActiveTopTab('management')} className={`flex-1 py-2.5 rounded-full text-[13px] font-bold transition-all ${activeTopTab === 'management' ? 'bg-[#F37024] text-white shadow-sm' : textSec}`} data-id="element-2848" data-mp="nmbjlr6qlgz1">
              ID Management
            </button>
          </div>
        </div>

        <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2849" data-mp="4o8msq7y6fqo">
          {activeTopTab === 'transfer' ? <>
              {/* Segmented Radio Selector */}
              <div className="flex gap-2 mb-6" data-id="element-2850" data-mp="240uozje1q2jf">
                {(['raast_id', 'account', 'iban'] as TransferMethod[]).map(method => <button key={method} onClick={() => {
              setTransferMethod(method);
              setFetchedDetails(null);
            }} className={`flex-1 py-3 rounded-xl text-[12px] font-bold transition-all border ${transferMethod === method ? `border-[#F37024] bg-[#F37024]/10 text-[#F37024]` : `${borderClr} ${cardBg} ${textSec}`}`} data-id="element-2851" data-mp="17wnqxn51rypl">
                      {method === 'raast_id' ? 'RAAST ID' : method === 'account' ? 'Account' : 'IBAN'}
                    </button>)}
              </div>

              {transferMethod === 'raast_id' ? <div className="flex-1 flex flex-col" data-id="element-2852" data-mp="29lzsyl6kr2jq">
                  {!fetchedDetails ? <>
                      <p className={`text-[14px] font-bold mb-3 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-2853" data-mp="2ekudpso3r16h">
                        Enter RAAST ID (Mobile Number)
                      </p>
                      <div className="relative mb-6" data-id="element-2854" data-mp="cntxt8skcyq7">
                        <SmartphoneIcon className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${textSec}`} data-id="element-2855" data-mp="cpdev1m1ndt1" />
                        <input type="tel" value={raastId} onChange={e => {
                  const val = e.target.value.replace(/\D/g, '');
                  if (val.length <= 11) setRaastId(val);
                }} placeholder="03XX XXXXXXX" className={`w-full rounded-2xl py-4 pl-12 pr-4 text-[16px] font-medium outline-none tracking-wide border ${isDark ? 'bg-[#3D2518] text-white placeholder:text-[#B8A89E]/70 border-[rgba(243,112,36,0.1)]' : 'bg-white text-[#303030] placeholder:text-[#818285] border-[#F0E8E3]'}`} data-id="element-2856" data-mp="lnz1p8yjtezm" />
                      </div>

                      <button onClick={handleProceedRaastId} disabled={!isValidRaastId || isFetching} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md disabled:opacity-50 flex items-center justify-center gap-2 mt-auto" data-id="element-2857" data-mp="yc4ri3dd9hrw">
                        {isFetching ? <>
                            <RefreshCwIcon className="w-5 h-5 animate-spin" data-id="element-2858" data-mp="1h6jsfyj8nkid" />
                            Fetching...
                          </> : 'Proceed'}
                      </button>
                    </> : <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="flex-1 flex flex-col" data-id="element-2859" data-mp="203bhyppdivuv">
                      <h3 className={`text-[13px] font-bold tracking-wider uppercase mb-4 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-2860" data-mp="2hb2n2lraybgp">
                        Recipient Details
                      </h3>
                      <div className={`rounded-2xl p-5 border ${cardBg} ${borderClr} mb-6`} data-id="element-2861" data-mp="2vbn9rt0c8rfh">
                        <div className="flex items-center gap-4 mb-4" data-id="element-2862" data-mp="303nd7vfma2gt">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2863" data-mp="350wxkwo5fnxs">
                            <ZapIcon className="w-6 h-6 text-[#F37024]" fill="currentColor" data-id="element-2864" data-mp="3f0xnx2kfr4bh" />
                          </div>
                          <div data-id="element-2865" data-mp="2kegtvh124p95">
                            <p className={`text-[16px] font-bold ${textPri}`} data-id="element-2866" data-mp="2z1tjphxwdh5c">
                              {fetchedDetails.name}
                            </p>
                            <p className={`text-[13px] ${textSec}`} data-id="element-2867" data-mp="2u9io2ra6whil">
                              RAAST ID: {fetchedDetails.mobile}
                            </p>
                          </div>
                        </div>
                        <div className={`flex items-center gap-2 p-3 rounded-xl ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-2868" data-mp="335rjluv1qvzg">
                          <CheckCircle2Icon className="w-5 h-5 text-[#22C55E]" data-id="element-2869" data-mp="3d2cpeiew4b4c" />
                          <span className={`text-[12px] font-medium ${textPri}`} data-id="element-2870" data-mp="1nng2ux1ka1yt">
                            RAAST ID Verified
                          </span>
                        </div>
                      </div>

                      <button onClick={() => navigateTo('enter_amount')} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md mt-auto" data-id="element-2871" data-mp="3547dayyxe3o">
                        Continue
                      </button>
                    </motion.div>}
                </div> : <div className={`text-center py-16 rounded-2xl border ${cardBg} ${borderClr}`} data-id="element-2872" data-mp="3fm9f1zrtniv1">
                  <BuildingIcon className={`w-12 h-12 mx-auto mb-3 ${textSec}`} data-id="element-2873" data-mp="31nsy7g08wbz6" />
                  <h3 className={`text-lg font-bold mb-1 ${textPri}`} data-id="element-2874" data-mp="36nfb1oc5vq7g">
                    Coming Soon
                  </h3>
                  <p className={`text-sm ${textSec}`} data-id="element-2875" data-mp="14tps2x7k3s3m">
                    This transfer method will be available shortly.
                  </p>
                </div>}
            </> : <div className={`rounded-2xl p-6 border ${cardBg} ${borderClr}`} data-id="element-2876" data-mp="2vxhxfubmd5c4">
              <div className="flex items-center justify-between mb-6" data-id="element-2877" data-mp="2rweev026sv77">
                <div data-id="element-2878" data-mp="2rulajrf1qn2d">
                  <p className={`text-[12px] font-medium uppercase tracking-wider ${textSec}`} data-id="element-2879" data-mp="2mxbmog7b2jgn">
                    Your RAAST ID
                  </p>
                  <p className={`text-[18px] font-bold mt-1 ${textPri} ${!isRaastIdEnabled ? 'opacity-40' : ''}`} data-id="element-2880" data-mp="2i033ccaf89dt">
                    0300 1234567
                  </p>
                </div>
                <div className={`px-3 py-1 rounded-full text-[11px] font-bold ${isRaastIdEnabled ? 'bg-[#22C55E]/10 text-[#22C55E]' : isDark || isPlatinum ? 'bg-white/10 text-[#B8A89E]' : 'bg-black/5 text-[#818285]'}`} data-id="element-2881" data-mp="247ddtxvoomby">
                  {isRaastIdEnabled ? 'Active' : 'Disabled'}
                </div>
              </div>

              {/* Enable/Disable Toggle Row */}
              <div className="flex items-center justify-between mb-5" data-id="element-2882" data-mp="eox12jkcyzpj">
                <div className="flex-1 pr-3" data-id="element-2883" data-mp="jot3wfhbkl3i">
                  <p className={`text-[13px] font-bold ${textPri}`} data-id="element-2884" data-mp="jq49mkubs2x9">
                    Enable RAAST ID
                  </p>
                  <p className={`text-[11px] mt-0.5 ${textSec}`} data-id="element-2885" data-mp="noac3go4rwcd">
                    {isRaastIdEnabled ? 'Others can send money to your RAAST ID' : 'Your RAAST ID is currently disabled'}
                  </p>
                </div>
                <button onClick={() => setShowToggleConfirm(true)} role="switch" aria-checked={isRaastIdEnabled} className={`relative shrink-0 w-12 h-7 rounded-full transition-colors duration-300 ${isRaastIdEnabled ? 'bg-[#F37024]' : isDark || isPlatinum ? 'bg-white/15' : 'bg-[#E5DDD7]'}`} data-id="element-2886" data-mp="l80c28ied">
                  <motion.span layout transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} className="absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md" style={{
                left: isRaastIdEnabled ? 'calc(100% - 26px)' : '2px'
              }} data-id="element-2887" data-mp="fldepjxght2f" />
                </button>
              </div>

              <div className={`h-px w-full ${borderClr} mb-5`} data-id="element-2888" data-mp="3nulwvxc61nu9" />
              <div className="flex items-center justify-between" data-id="element-2889" data-mp="5npgul4k05g1">
                <div className={!isRaastIdEnabled ? 'opacity-40' : ''} data-id="element-2890" data-mp="5lussxxzjx7q">
                  <p className={`text-[13px] font-medium ${textPri}`} data-id="element-2891" data-mp="1lkpjt1kvkwk">
                    Linked Account
                  </p>
                  <p className={`text-[12px] mt-0.5 ${textSec}`} data-id="element-2892" data-mp="1h57a2qoidmob">
                    {linkedAccount.label} ••••
                    {linkedAccount.number.replace(/\D/g, '').slice(-4)}
                  </p>
                </div>
                <button onClick={() => setShowManageSheet(true)} disabled={!isRaastIdEnabled} className="px-4 py-2 rounded-xl text-[13px] font-bold border border-[#F37024] text-[#F37024] hover:bg-[#F37024]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-2893" data-mp="1pjv8cxaarnoh">
                  Manage
                </button>
              </div>
            </div>}
        </div>

        {/* Toggle Confirmation Modal */}
        <AnimatePresence data-id="element-2894" data-mp="1tlzwjghz7s3q">
          {showToggleConfirm && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/50 backdrop-blur-sm" onClick={() => setShowToggleConfirm(false)} data-id="element-2895" data-mp="1tlgi49rjblug">
              <motion.div initial={{
            scale: 0.9,
            opacity: 0,
            y: 20
          }} animate={{
            scale: 1,
            opacity: 1,
            y: 0
          }} exit={{
            scale: 0.95,
            opacity: 0,
            y: 10
          }} transition={{
            type: 'spring',
            damping: 22,
            stiffness: 280
          }} onClick={e => e.stopPropagation()} className={`w-full max-w-[340px] rounded-3xl p-6 shadow-2xl ${cardBg} border ${borderClr}`} data-id="element-2896" data-mp="r0pgbfolj92i">
                <div className="flex flex-col items-center text-center" data-id="element-2897" data-mp="3lnjelkrrgc8b">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isRaastIdEnabled ? 'bg-[#F37024]/10' : 'bg-[#22C55E]/10'}`} data-id="element-2898" data-mp="3gogaq3nioc1x">
                    <AlertTriangleIcon className={`w-8 h-8 ${isRaastIdEnabled ? 'text-[#F37024]' : 'text-[#22C55E]'}`} strokeWidth={2} data-id="element-2899" data-mp="3bj312y1r777o" />
                  </div>
                  <h3 className={`text-[18px] font-bold mb-2 ${textPri}`} data-id="element-2900" data-mp="3l9e3uh9pbacz">
                    Are you sure?
                  </h3>
                  <p className={`text-[13px] leading-relaxed mb-6 ${textSec}`} data-id="element-2901" data-mp="3pahl987edegb">
                    {isRaastIdEnabled ? 'Disabling your RAAST ID means others will no longer be able to send you money using your mobile number.' : 'Enabling your RAAST ID will allow others to send you money instantly using your mobile number.'}
                  </p>
                  <div className="flex gap-3 w-full" data-id="element-2902" data-mp="24wgxxn3h639v">
                    <button onClick={() => setShowToggleConfirm(false)} className={`flex-1 py-3 rounded-2xl font-bold text-[14px] border-2 ${isDark || isPlatinum ? 'border-[rgba(243,112,36,0.3)] text-[#F37024]' : 'border-[#F0E8E3] text-[#303030]'}`} data-id="element-2903" data-mp="29tply4bb11j1">
                      No
                    </button>
                    <button onClick={() => {
                  setIsRaastIdEnabled(prev => !prev);
                  setShowToggleConfirm(false);
                }} className="flex-1 py-3 rounded-2xl font-bold text-[14px] bg-[#F37024] text-white shadow-md" data-id="element-2904" data-mp="qri4pjt4a467">
                      Yes
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        {/* Manage Account Bottom Sheet */}
        <AnimatePresence data-id="element-2905" data-mp="3bcqocb49x48w">
          {showManageSheet && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowManageSheet(false)} data-id="element-2906" data-mp="3g9qx4tsicl3p">
              <motion.div initial={{
            y: '100%'
          }} animate={{
            y: 0
          }} exit={{
            y: '100%'
          }} transition={{
            type: 'spring',
            damping: 28,
            stiffness: 280
          }} onClick={e => e.stopPropagation()} className={`w-full max-w-[430px] rounded-t-3xl p-6 pb-8 shadow-2xl ${cardBg}`} data-id="element-2907" data-mp="2u2mrxbap61xc">
                <div className="flex justify-center mb-4" data-id="element-2908" data-mp="26a6cz9m9aq29">
                  <div className={`w-10 h-1.5 rounded-full ${isDark || isPlatinum ? 'bg-white/20' : 'bg-[#E5DDD7]'}`} data-id="element-2909" data-mp="229mh09df5shp" />
                </div>
                <div className="flex items-center justify-between mb-1" data-id="element-2910" data-mp="28rh2wdhpllss">
                  <h3 className={`text-[18px] font-bold ${textPri}`} data-id="element-2911" data-mp="1ttoqvvbdffjc">
                    Change Linked Account
                  </h3>
                  <button onClick={() => setShowManageSheet(false)} className={`p-1 -mr-1 ${textSec}`} data-id="element-2912" data-mp="1yrqaqigmfivv">
                    <XIcon className="w-5 h-5" data-id="element-2913" data-mp="1ps38xvjzcb2a" />
                  </button>
                </div>
                <p className={`text-[13px] mb-5 ${textSec}`} data-id="element-2914" data-mp="1b373855eba4u">
                  Select an account to link with your RAAST ID
                </p>

                <div className="space-y-3" data-id="element-2915" data-mp="1g3bhdoitzsrw">
                  {PAY_FROM_ACCOUNTS.map(acc => {
                const isLinked = acc.id === linkedAccountId;
                return <button key={acc.id} onClick={() => {
                  if (isLinked) return;
                  setLinkedAccountId(acc.id);
                  setShowManageSheet(false);
                  setShowLinkSuccess({
                    accountLabel: acc.label
                  });
                  setTimeout(() => setShowLinkSuccess(null), 2500);
                }} className={`w-full text-left rounded-2xl p-4 flex items-center gap-4 border-2 transition-all ${isLinked ? 'border-[#F37024] bg-[#F37024]/5' : `${borderClr} ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`}`} data-id="element-2916" data-mp="16317odw093hh">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${isDark || isPlatinum ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2917" data-mp="1b5mnhoxl798o">
                          <BuildingIcon className="w-5 h-5 text-[#F37024]" strokeWidth={2} data-id="element-2918" data-mp="1k6kri780zgr9" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-2919" data-mp="288z7p22zqtrk">
                          <div className="flex items-center gap-2" data-id="element-2920" data-mp="287edxeoi3eur">
                            <p className={`text-[15px] font-bold ${textPri}`} data-id="element-2921" data-mp="2d10uvj1p64wd">
                              {acc.label}
                            </p>
                            {isLinked && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#F37024] text-white" data-id="element-2922" data-mp="1lavzyqgb9zgw">
                                Linked
                              </span>}
                          </div>
                          <p className={`text-[12px] ${textSec}`} data-id="element-2923" data-mp="1z8swxnyog7w3">
                            {acc.number}
                          </p>
                          <p className={`text-[11px] mt-0.5 ${textSec}`} data-id="element-2924" data-mp="35od7p1i22ych">
                            Balance: PKR {acc.balance.toLocaleString()}
                          </p>
                        </div>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 border-2 ${isLinked ? 'bg-[#F37024] border-[#F37024]' : isDark || isPlatinum ? 'border-white/20' : 'border-[#E5DDD7]'}`} data-id="element-2925" data-mp="z0cu3a5kq9sn">
                          {isLinked && <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-2926" data-mp="k9x5dldy8jpq" />}
                        </div>
                      </button>;
              })}
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        {/* Link Success Popup */}
        <AnimatePresence data-id="element-2927" data-mp="20qhx0sjdp8x0">
          {showLinkSuccess && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/50 backdrop-blur-sm" onClick={() => setShowLinkSuccess(null)} data-id="element-2928" data-mp="20nvmck16ydak">
              <motion.div initial={{
            scale: 0.85,
            opacity: 0,
            y: 20
          }} animate={{
            scale: 1,
            opacity: 1,
            y: 0
          }} exit={{
            scale: 0.95,
            opacity: 0
          }} transition={{
            type: 'spring',
            damping: 18,
            stiffness: 240
          }} onClick={e => e.stopPropagation()} className={`w-full max-w-[320px] rounded-3xl p-6 shadow-2xl text-center ${cardBg} border ${borderClr}`} data-id="element-2929" data-mp="3l80tjzt98bkk">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              damping: 14,
              stiffness: 220,
              delay: 0.1
            }} className="w-16 h-16 mx-auto bg-[#22C55E] rounded-full flex items-center justify-center mb-4 shadow-lg shadow-[#22C55E]/30" data-id="element-2930" data-mp="2kz2hlose44rs">
                  <CheckCircle2Icon className="w-9 h-9 text-white" strokeWidth={2.5} data-id="element-2931" data-mp="1x8ew20pjxgmr" />
                </motion.div>
                <h3 className={`text-[18px] font-bold mb-2 ${textPri}`} data-id="element-2932" data-mp="277xeudtq6wd8">
                  Account Updated
                </h3>
                <p className={`text-[13px] leading-relaxed ${textSec}`} data-id="element-2933" data-mp="1jh0apn8693cj">
                  Your RAAST ID is now linked to{' '}
                  <span className={`font-bold ${textPri}`} data-id="element-2934" data-mp="t0yn1rjrwycw">
                    {showLinkSuccess.accountLabel}
                  </span>{' '}
                  successfully.
                </p>
                <button onClick={() => setShowLinkSuccess(null)} className="mt-5 w-full py-3 rounded-2xl font-bold text-[14px] bg-[#F37024] text-white shadow-md" data-id="element-2935" data-mp="12ywc2h9giz6k">
                  Done
                </button>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </motion.div>;
  };
  const renderEnterAmount = () => {
    return <motion.div key="enter_amount" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2936" data-mp="1lp4baoa1d5hh">
        <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-2937" data-mp="1unzhw1gzshlb">
          <button onClick={() => navigateTo('tabs', -1)} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2938" data-mp="1zposjg0ayt8v">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-2939" data-mp="3f6jpvebsq8r4" />
          </button>
          <h1 className={`text-lg font-bold flex-1 ml-3 ${textPri}`} data-id="element-2940" data-mp="3b7ay2f0bgky7">
            Enter Amount
          </h1>
        </div>

        <div className="px-6 mt-2 flex-1 flex flex-col pb-8" data-id="element-2941" data-mp="369ht1par1wzi">
          {/* Pay From Account Selector */}
          <div className="mb-8 relative z-20" data-id="element-2942" data-mp="6u4ci9xydaik">
            <h3 className={`text-[13px] font-bold tracking-wider uppercase mb-3 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-2943" data-mp="2qg6vwsvsozy">
              Pay From
            </h3>
            <button onClick={() => setShowPayFromDropdown(!showPayFromDropdown)} className={`w-full rounded-2xl p-4 flex items-center justify-between border shadow-sm ${isDark ? 'bg-[#2D1810] border-[rgba(243,112,36,0.1)]' : 'bg-white border-[#F0E8E3]'}`} data-id="element-2944" data-mp="3p72f6om4qhgf">
              <div className="text-left" data-id="element-2945" data-mp="dikp9tsqvuy0">
                <p className={`text-[15px] font-bold ${textPri}`} data-id="element-2946" data-mp="dlf7deoad3o9">
                  {payFromAccount.label}
                </p>
                <p className={`text-[13px] ${textSec}`} data-id="element-2947" data-mp="nh8shmg2m5hn">
                  {payFromAccount.number}
                </p>
              </div>
              <ChevronDownIcon className={`w-5 h-5 ${textSec}`} data-id="element-2948" data-mp="ihnsr9t2tt3v" />
            </button>
            <p className={`text-[12px] mt-2 ml-2 ${textSec}`} data-id="element-2949" data-mp="whf1av0tqhah">
              Available Balance: PKR {payFromAccount.balance.toLocaleString()}
              .00
            </p>

            <AnimatePresence data-id="element-2950" data-mp="167qgxetveex7">
              {showPayFromDropdown && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className={`absolute top-[80px] left-0 right-0 rounded-2xl shadow-xl overflow-hidden border ${isDark ? 'bg-[#3D2518] border-[rgba(243,112,36,0.2)]' : 'bg-white border-[#F0E8E3]'}`} data-id="element-2951" data-mp="16a4dxt84217e">
                  {PAY_FROM_ACCOUNTS.map(acc => <button key={acc.id} onClick={() => {
                setPayFromAccount(acc);
                setShowPayFromDropdown(false);
              }} className={`w-full text-left p-4 border-b last:border-0 ${isDark ? 'border-[rgba(243,112,36,0.1)] hover:bg-white/5' : 'border-[#F0E8E3] hover:bg-black/5'}`} data-id="element-2952" data-mp="1ptjpm7b1qptx">
                      <p className={`font-bold ${textPri}`} data-id="element-2953" data-mp="174dcr0wxosg1">{acc.label}</p>
                      <p className={`text-sm ${textSec}`} data-id="element-2954" data-mp="s5jc1bgnq16e">{acc.number}</p>
                    </button>)}
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Amount Input */}
          <div className="flex flex-col items-center mb-auto" data-id="element-2955" data-mp="e22fp6869tir">
            <p className={`text-[13px] font-bold mb-4 uppercase tracking-wider ${isDark ? 'text-[#F37024]' : 'text-[#661806]'}`} data-id="element-2956" data-mp="999g0wrk9bdj">
              Currency: PKR
            </p>
            <div className={`flex items-center gap-4 border-b-2 pb-2 min-w-[200px] justify-center ${isDark ? 'border-[rgba(243,112,36,0.3)]' : 'border-[#F0E8E3]'}`} data-id="element-2957" data-mp="3vhsft8a1ddo0">
              <span className={`text-[24px] font-bold ${textSec}`} data-id="element-2958" data-mp="rzr4uq99vmtm">PKR</span>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" className={`w-[140px] text-[40px] font-bold bg-transparent outline-none text-center ${textPri} placeholder:opacity-30`} autoFocus data-id="element-2959" data-mp="n17dgabwtitv" />
            </div>

            <div className="flex gap-2 mt-8" data-id="element-2960" data-mp="32r0p2qbqph82">
              {[200, 500, 1000, 2000].map(val => <button key={val} onClick={() => setAmount(val.toString())} className={`px-4 py-2 rounded-full text-[13px] font-bold transition-colors ${amount === val.toString() ? 'bg-[#F37024] text-white' : `${inputBg} ${textPri}`}`} data-id="element-2961" data-mp="1ic45xzbfroan">
                  Rs. {val}
                </button>)}
            </div>
          </div>

          <button onClick={() => navigateTo('review')} disabled={!amount || parseInt(amount) <= 0} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md disabled:opacity-50 mt-6" data-id="element-2962" data-mp="220d3kovr0mhj">
            Next
          </button>
        </div>
      </motion.div>;
  };
  const renderReview = () => {
    return <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2963" data-mp="gms1kumfs8dc">
        <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-2964" data-mp="272kl2ygvgt5i">
          <button onClick={() => navigateTo('enter_amount', -1)} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2965" data-mp="274f4u9geqf80">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-2966" data-mp="1tanwv5ibr3ee" />
          </button>
          <h1 className={`text-lg font-bold flex-1 ml-3 ${textPri}`} data-id="element-2967" data-mp="1objogh3859p4">
            Review Transfer
          </h1>
        </div>

        <div className="px-6 mt-2 flex-1 flex flex-col pb-8" data-id="element-2968" data-mp="223hnu074vr83">
          <div className={`rounded-[24px] p-6 shadow-sm mb-auto border ${cardBg} ${borderClr}`} data-id="element-2969" data-mp="224sv49zsfh3y">
            <div className="flex flex-col items-center mb-8 text-center" data-id="element-2970" data-mp="19mf7124979i2">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2971" data-mp="19karfmwzsm1y">
                <ZapIcon className="w-8 h-8 text-[#F37024]" fill="currentColor" data-id="element-2972" data-mp="vfk8ppr0ouni" />
              </div>
              <p className={`text-[28px] font-bold ${textPri}`} data-id="element-2973" data-mp="lj7gkwb4eouv">
                Rs. {parseInt(amount || '0').toLocaleString()}
              </p>
              <p className={`text-[14px] mt-1 ${textSec}`} data-id="element-2974" data-mp="qik0b86p3ft8">
                To {fetchedDetails?.name || 'Recipient'}
              </p>
            </div>

            <div className="space-y-4" data-id="element-2975" data-mp="1j6fv27cpexnd">
              <div className="flex justify-between" data-id="element-2976" data-mp="1j5egvu0e5v1f">
                <span className={textSec} data-id="element-2977" data-mp="149p48mh3iu9d">From Account</span>
                <span className={`font-bold ${textPri}`} data-id="element-2978" data-mp="14972xbfktpul">
                  {payFromAccount.number.slice(-4)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-2979" data-mp="3oz632yqdqumn">
                <span className={textSec} data-id="element-2980" data-mp="3p1se58chbs9m">Transfer Method</span>
                <span className={`font-bold ${textPri}`} data-id="element-2981" data-mp="3tztxludthtlm">RAAST</span>
              </div>
              <div className="flex justify-between" data-id="element-2982" data-mp="20gbhpo5gfary">
                <span className={textSec} data-id="element-2983" data-mp="20huwtbfdjfs5">RAAST ID</span>
                <span className={`font-bold ${textPri}`} data-id="element-2984" data-mp="1vi9vwvgn8xcs">
                  {fetchedDetails?.mobile || raastId}
                </span>
              </div>
              <div className={`h-px w-full ${borderClr} my-2`} data-id="element-2985" data-mp="1lr5gxvqyxchy" />
              <div className="flex justify-between" data-id="element-2986" data-mp="1lqvqy85xb5c9">
                <span className={textSec} data-id="element-2987" data-mp="1gskia0rd70vm">Fee</span>
                <span className={`font-bold ${textPri}`} data-id="element-2988" data-mp="1gpy9jxsqmfbt">Rs. 0</span>
              </div>
              <div className="flex justify-between" data-id="element-2989" data-mp="1cowbhkak49bf">
                <span className={`font-bold ${textPri}`} data-id="element-2990" data-mp="17qur8w1ea3yd">Total Amount</span>
                <span className={`font-bold text-[#F37024]`} data-id="element-2991" data-mp="12s9oopyo786r">
                  Rs. {parseInt(amount || '0').toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <button onClick={() => navigateTo('mpin')} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md mt-6" data-id="element-2992" data-mp="2x6aww1g417ji">
            Confirm & Transfer
          </button>
        </div>
      </motion.div>;
  };
  const renderMpin = () => {
    return <motion.div key="mpin" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col items-center justify-center ${bgStyle}`} data-id="element-2993" data-mp="3ef3gyd26dtsc">
        <AnimatePresence mode="wait" data-id="element-2994" data-mp="2ltc0mproytcq">
          {biometricPhase === 'scanning' && <motion.div key="scanning" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.8
        }} className="flex flex-col items-center gap-6" data-id="element-2995" data-mp="2qs52fkxm8vx2">
              <motion.div animate={{
            scale: [1, 1.2, 1],
            opacity: [0.6, 1, 0.6]
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="w-24 h-24 rounded-full flex items-center justify-center" style={{
            background: 'rgba(243, 112, 36, 0.12)',
            border: '2px solid rgba(243, 112, 36, 0.3)'
          }} data-id="element-2996" data-mp="1121nc5bbl80z">
                {scanType === 'thumb' ? <FingerprintIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-2997" data-mp="1tqh3iv66twdw" /> : <ScanFaceIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-2998" data-mp="1optay52b97hk" />}
              </motion.div>
              <div className="text-center" data-id="element-2999" data-mp="3ql3pmp4yuuku">
                <p className={`text-[18px] font-bold mb-1 ${textPri}`} data-id="element-3000" data-mp="260f402mgoq1k">
                  {scanType === 'thumb' ? 'Scanning Fingerprint' : 'Scanning Face'}
                </p>
                <p className={`text-[13px] ${textSec}`} data-id="element-3001" data-mp="1wa3ywcr1kat3">
                  {scanType === 'thumb' ? 'Place your finger on the sensor' : 'Look at the camera'}
                </p>
              </div>
            </motion.div>}

          {biometricPhase === 'verified' && <motion.div key="verified" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.8
        }} className="flex flex-col items-center gap-6" data-id="element-3002" data-mp="1sb4uqud4wi2p">
              <motion.div initial={{
            scale: 0.3
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            damping: 12,
            stiffness: 200
          }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center" style={{
            border: '2px solid rgba(34, 197, 94, 0.3)'
          }} data-id="element-3003" data-mp="3cnausuibbp1w">
                <CheckCircle2Icon className="w-12 h-12 text-[#22C55E]" strokeWidth={1.5} data-id="element-3004" data-mp="y1a97lj9wqqd" />
              </motion.div>
              <div className="text-center" data-id="element-3005" data-mp="p8q5i6o4jvyo">
                <p className={`text-[18px] font-bold mb-1 ${textPri}`} data-id="element-3006" data-mp="p9rt1ambuaxa">
                  Verified Successfully
                </p>
                <p className={`text-[13px] ${textSec}`} data-id="element-3007" data-mp="adixaj1nafpm">
                  Processing transaction...
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.div>;
  };
  const renderProcessing = () => {
    return <motion.div key="processing" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className={`w-full min-h-screen flex flex-col items-center justify-center ${bgStyle}`} data-id="element-3008" data-mp="3ntut2uys4lew">
        <motion.div animate={{
        rotate: 360
      }} transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: 'linear'
      }} className="w-16 h-16 border-4 border-[#F37024]/20 border-t-[#F37024] rounded-full mb-6" data-id="element-3009" data-mp="ema8mdijolpu" />
        <h2 className={`text-xl font-bold mb-2 ${textPri}`} data-id="element-3010" data-mp="jeuzp7a2gkpo">
          Processing Transfer
        </h2>
        <p className={`text-sm ${textSec}`} data-id="element-3011" data-mp="ybu7av4x4dk5">
          Please wait while we process your request...
        </p>
      </motion.div>;
  };
  const renderSuccess = () => {
    return <motion.div key="success" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-3012" data-mp="10h0n94ms8ega">
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12" data-id="element-3013" data-mp="1abshpzl2il6u">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          damping: 15,
          stiffness: 200,
          delay: 0.1
        }} className="w-24 h-24 bg-[#22C55E] rounded-full flex items-center justify-center mb-6 shadow-lg shadow-[#22C55E]/30" data-id="element-3014" data-mp="1f66kin227ulj">
            <CheckCircle2Icon className="w-12 h-12 text-white" strokeWidth={2.5} data-id="element-3015" data-mp="3uvzqamf7kyrt" />
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className={`text-2xl font-bold mb-2 text-center ${textPri}`} data-id="element-3016" data-mp="3q83nx0n449r">
            Transfer Successful!
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className={`text-[15px] text-center mb-8 ${textSec}`} data-id="element-3017" data-mp="3dnqdmxl63iir">
            Your transfer of{' '}
            <span className="font-bold text-[#F37024]" data-id="element-3018" data-mp="80v2p1p8pos">
              Rs. {parseInt(amount || '0').toLocaleString()}
            </span>{' '}
            to {fetchedDetails?.name || 'Recipient'} has been completed
            successfully via RAAST.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className={`w-full rounded-[24px] p-6 shadow-sm border ${cardBg} ${borderClr}`} data-id="element-3019" data-mp="2l02yhy2o1k3i">
            <div className="space-y-4" data-id="element-3020" data-mp="8nyhrl7tr4c2">
              <div className="flex justify-between" data-id="element-3021" data-mp="wd195ks9gbjr">
                <span className={textSec} data-id="element-3022" data-mp="wec9tbqet36n">Reference ID</span>
                <span className={`font-bold ${textPri}`} data-id="element-3023" data-mp="11cnmrvc03tt3">
                  RST-{Math.floor(Math.random() * 1000000000)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3024" data-mp="mgxuxgk4wmgg">
                <span className={textSec} data-id="element-3025" data-mp="rd521co9o3zz">Date & Time</span>
                <span className={`font-bold ${textPri}`} data-id="element-3026" data-mp="3752f83yiwpba">
                  {new Date().toLocaleDateString('en-PK', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
                  ,{' '}
                  {new Date().toLocaleTimeString('en-PK', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3027" data-mp="236filt1r7kj5">
                <span className={textSec} data-id="element-3028" data-mp="1z5di78w3p2fl">RAAST ID</span>
                <span className={`font-bold ${textPri}`} data-id="element-3029" data-mp="1z3iytz0h9idm">
                  {fetchedDetails?.mobile || raastId}
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="px-6 pb-8 flex gap-3" data-id="element-3030" data-mp="16f4xb9a6k3xb">
          <button className={`flex-1 py-4 rounded-2xl font-bold text-[15px] flex items-center justify-center gap-2 border-2 transition-colors ${isDark ? 'border-[rgba(243,112,36,0.3)] text-[#F37024] hover:bg-[#F37024]/10' : 'border-[#F0E8E3] text-[#303030] hover:bg-black/5'}`} data-id="element-3031" data-mp="rpyxpk7fkbow">
            <ShareIcon className="w-5 h-5" data-id="element-3032" data-mp="2rs8d5nd4x6f8" />
            Share
          </button>
          <button onClick={handleGoHome} className="flex-[2] py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md" data-id="element-3033" data-mp="2wltbjgin42ey">
            Done
          </button>
        </motion.div>
      </motion.div>;
  };
  if (view === 'hub') return <RaastHome onNavigate={v => setView(v as View)} onClose={onClose} data-id="element-3034" data-mp="28vyzh165ahvr" />;
  if (view === 'register') return <RaastRegister onBack={handleBackToHub} onHome={handleGoHome} data-id="element-3035" data-mp="2dt8m6925gffw" />;
  if (view === 'relink') return <RaastRelinkDelink onBack={handleBackToHub} onHome={handleGoHome} data-id="element-3036" data-mp="2iuxw1ldmyn2e" />;
  if (view === 'merchant') return <RaastMerchantPayments onBack={handleBackToHub} onHome={handleGoHome} onTransfer={() => setView('transfer')} data-id="element-3037" data-mp="3pc1jygndj8gc" />;
  if (view === 'qr_till') return <RaastQrTill onBack={handleGoHome} onHome={handleGoHome} data-id="element-3038" data-mp="1ikn8nud6ayve" />;
  if (view === 'history') return <RaastHistory onBack={handleBackToHub} onHome={handleGoHome} data-id="element-3039" data-mp="1dn54eha3p7tq" />;
  if (view === 'requests') return <RaastRequests onBack={handleBackToHub} onHome={handleGoHome} data-id="element-3040" data-mp="18tsl8mo6xt8d" />;
  return <AnimatePresence mode="wait" custom={direction} data-id="element-3041" data-mp="13vr0lxb49lus">
      {step === 'tabs' && renderTabs()}
      {step === 'enter_amount' && renderEnterAmount()}
      {step === 'review' && renderReview()}
      {step === 'mpin' && renderMpin()}
      {step === 'processing' && renderProcessing()}
      {step === 'success' && renderSuccess()}
    </AnimatePresence>;
}